import React, { useState, useEffect, useMemo } from 'react';
import Card from '@common/components/card';
import AnswerCommentBox from '@modules/performance/components/answer-comment-box/answer-comment-box';
import { useEvaluationStore } from './store';
import { useAppSelector } from '@common/hooks';
import * as loggedUserSelector from '@modules/core/selectors/logged-user';
import { QuestionIcon } from '@modules/performance/components/question-icon/question-icon';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import { useTranslation } from 'react-i18next';
import { useStickyHeader } from './hooks/use-sticky-header';
import { RatingType } from '@modules/performance/types';
import { getRatingColor } from '../performance-review/components';

interface TalentReviewAnswer {
  potential: number;
  performance: number;
  retention_risk: number;
  impact_of_loss: string;
}


const TalentReview: React.FC<{ componentId?: string }> = ({
  componentId = '29',
}) => {
  const { evaluation, responsesByComponentId, setComponentAnswer } =
    useEvaluationStore();
  const { t } = useTranslation();

  const loggedUser = useAppSelector(loggedUserSelector.selected);
  const reviewer = evaluation?.data?.reviewers?.[0];
  const reviewee = evaluation?.data?.reviewee;
  const reviewerId = reviewer?.id ? String(reviewer.id) : undefined;
  const revieweeId = reviewee?.id ? String(reviewee.id) : undefined;
  const isReviewerViewing =
    String(loggedUser?.id || '') === String(reviewerId || '');

  const selfSubmitted =
    evaluation?.data?.reviewee?.status === 'submitted' ||
    Boolean(evaluation?.data?.reviewee?.submitted_at);
  // const managerSubmitted = Array.isArray(evaluation?.data?.reviewers)
  //   ? evaluation?.data?.reviewers.some(
  //       (r: any) => r?.status === 'submitted' || Boolean(r?.submitted_at)
  //     )
  //   : false;
  const isCompleted = Boolean(evaluation?.data?.completed_at);

  const currentUserId = isReviewerViewing ? reviewerId : revieweeId;
  const entry = responsesByComponentId?.[String(componentId)];
  const rawAnswer = currentUserId
    ? entry?.answersByUserId?.[currentUserId]
    : undefined;
  const settings = evaluation?.data?.form?.components?.find(
    (c: any) => c.id === componentId
  )?.settings;
  const isShowImpactOfLoss = settings?.impact_of_loss;
  const isShowRetentionRisk = settings?.retention_risk;
  const existingAnswer: TalentReviewAnswer = useMemo(() => {
    if (!rawAnswer) {
      return {
        potential: 2,
        performance: 2,
        retention_risk: 2,
        impact_of_loss: null,
      };
    }

    let answerData = rawAnswer;
    if (typeof rawAnswer === 'object' && rawAnswer.answer) {
      answerData = rawAnswer.answer;
    }

    if (typeof answerData === 'object') {
      return {
        potential: answerData.potential || 2,
        performance: answerData.performance || 2,
        retention_risk: answerData.retention_risk || 2,
        impact_of_loss: answerData.impact_of_loss || null,
      };
    }

    return {
      potential: 2,
      performance: 2,
      retention_risk: 2,
      impact_of_loss: null,
    };
  }, [rawAnswer]);

  const [formData, setFormData] = useState<TalentReviewAnswer>(existingAnswer);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setFormData(existingAnswer);
  }, [existingAnswer]);

  useEffect(() => {
    if (!isInitialized && !rawAnswer && currentUserId && componentId) {
      setComponentAnswer(String(componentId), currentUserId, {
        answer: existingAnswer,
      });
      setIsInitialized(true);
    }
  }, [
    isInitialized,
    rawAnswer,
    currentUserId,
    componentId,
    existingAnswer,
    setComponentAnswer,
  ]);

  const getLabel = (value: number) => {
    switch (value) {
      case 1:
        return t('performance:low');
      case 2:
        return t('performance:moderate');
      case 3:
        return t('performance:high');
      default:
        return t('performance:moderate');
    }
  };

  const handleRatingChange = (type: RatingType, value: number) => {
    if (isCompleted || (!isReviewerViewing && selfSubmitted)) return;

    const newData = {
      ...formData,
      [type]: value,
    };

    setFormData(newData);

    if (currentUserId) {
      setComponentAnswer(String(componentId), currentUserId, {
        answer: newData,
      });
    }
  };

  const handleImpactOfLossChange = (value: string) => {
    if (isCompleted || (!isReviewerViewing && selfSubmitted)) return;

    const newData = {
      ...formData,
      impact_of_loss: value,
    };

    setFormData(newData);

    if (currentUserId) {
      setComponentAnswer(String(componentId), currentUserId, {
        answer: newData,
      });
    }
  };

  const { sentinelRef, stickyHeaderRef, isHeaderSticky, defaultTopOffset } =
    useStickyHeader();

  const RatingComponent: React.FC<{
    title: string;
    description: string;
    value: number;
    type: RatingType;
  }> = ({ title, description, value, type }) => {
    const isReadOnly = isCompleted || (!isReviewerViewing && selfSubmitted);

    return (
      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
        <div className="tw-text-lg tw-font-semibold tw-text-gray-900">
          {title}
        </div>
        <div className="tw-text-sm tw-font-normal tw-text-gray-600">
          {description}
        </div>
        <div className="tw-max-w-max">
          <div className="tw-flex tw-border tw-border-gray-200 tw-border-solid tw-rounded-lg tw-overflow-hidden">
            {[1, 2, 3].map((ratingValue, index) => {
              const isSelected = value === ratingValue;
              const colorClass = getRatingColor(type, ratingValue);

              return (
                <React.Fragment key={ratingValue}>
                  <div
                    onClick={() => handleRatingChange(type, ratingValue)}
                    className={`tw-text-center tw-flex-1 tw-min-w-20 tw-font-medium tw-p-2 tw-transition-colors ${
                      isSelected
                        ? `${colorClass} tw-bg-opacity-10 tw-text-white`
                        : 'tw-text-gray-textGray hover:tw-bg-gray-50'
                    } ${
                      isReadOnly ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                    }`}
                  >
                    {ratingValue}
                  </div>
                  {index < 2 && (
                    <div className="tw-w-px tw-border tw-border-solid tw-border-gray-light-50" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="tw-flex tw-justify-center">
            <div className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium">
              {getLabel(value)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div ref={sentinelRef} className="tw-h-0 tw-w-full tw-mb-[-16px]" />
      <div
        ref={stickyHeaderRef}
        style={
          {
            zIndex: 1,
            '--top-offset': `${defaultTopOffset ?? 203}px`,
          } as React.CSSProperties
        }
        className={`${
          isHeaderSticky
            ? '!tw-bg-white !tw-p-5 !tw-border-b tw-border-0 tw-border-solid !tw-border-gray-border' +
              ' tw-sticky tw-top-[var(--top-offset)] tw-transition-all tw-duration-200'
            : ''
        }`}
      >
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
          <QuestionIcon
            type={PerformanceQuestionType.TALENT_REVIEW}
            className="!tw-w-4 !tw-h-4"
          />
          <div className="tw-text-gray-textDark tw-text-xl tw-font-semibold">
            {t('performance:question_type_title_talent_review')}
          </div>
        </div>
      </div>
      <Card>
        <Card.Content className="tw-flex tw-flex-col !tw-p-5 !tw-gap-5">
          <RatingComponent
            title={t('performance:rate_performance_of_employee')}
            description={t('performance:rate_employee_performance_description')}
            value={formData.performance}
            type={RatingType.PERFORMANCE}
          />

          <RatingComponent
            title={t('performance:rate_potential_of_employee')}
            description={t('performance:rate_potential_description')}
            value={formData.potential}
            type={RatingType.POTENTIAL}
          />

          {isShowRetentionRisk && (
            <RatingComponent
              title={t('performance:rate_retention_risk_of_employee')}
              description={t('performance:rate_retention_risk_description')}
              value={formData.retention_risk}
              type={RatingType.RETENTION_RISK}
            />
          )}

          {isShowImpactOfLoss && (
            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
              <div className="tw-text-lg tw-font-semibold tw-text-gray-900">
                {t('performance:enter_impact_of_loss_for_employee')}
              </div>
              <div className="tw-text-sm tw-font-normal tw-text-gray-600">
                {t('performance:loss_for_employee_description')}
              </div>
              <AnswerCommentBox
                userFullName={
                  isReviewerViewing
                    ? reviewer?.full_name || ''
                    : reviewee?.full_name || ''
                }
                answer={formData.impact_of_loss}
                isReadOnly={
                  isCompleted || (!isReviewerViewing && selfSubmitted)
                }
                canEdit={!isCompleted && (isReviewerViewing || !selfSubmitted)}
                showAsNotSubmitted={
                  (isReviewerViewing || !selfSubmitted) &&
                  !formData.impact_of_loss
                }
                enableInlineEdit={
                  !isCompleted && (isReviewerViewing || !selfSubmitted)
                }
                onSave={handleImpactOfLossChange}
              />
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default TalentReview;
